import type { ValidationError } from '@/types/api'
import { uebersetze } from '@/lib/uebersetzen'

/**
 * Warum der Server einen Text nicht gespeichert hat, als Satz in der Sprache
 * des Lesers.
 *
 * Der Server schickt zu jedem abgelehnten Feld die Regel (`keyword`), ihre
 * Grenzen (`args`) und den Wert, dazu einen deutschen Satz. Den Satz zeigte
 * der Editor bis zum 08.10.2026 an, auch einem englischen Zugang („Höchstens 80
 * Zeichen erlaubt"). Jetzt entsteht er hier. Der deutsche bleibt Rückfall für
 * eine Regel, die das Panel nicht kennt.
 *
 * Hat die Seite für die Regel einen eigenen Satz (`meldung`, aus schemas.ts),
 * gilt der, wörtlich wie die Beschriftung desselben Feldes. Bei Bangkoks
 * Preisen ist das „Nur der Preis in ganzen Euro, etwa 55“ statt der Formel.
 */
export function pruefsatz(f: ValidationError): string {
  if (typeof f.meldung === 'string' && f.meldung.trim() !== '') return f.meldung.trim()
  const args = f.args ?? {}
  // Zeichen wie der Server sie zählt (mb_strlen), nicht UTF-16-Einheiten.
  const laenge = typeof f.value === 'string' ? [...f.value].length : 0
  switch (f.keyword) {
    case 'maxLength':
      return typeof args.max === 'number'
        ? uebersetze('meldung.pruefung.zu_lang', { max: args.max, laenge })
        : uebersetze('meldung.pruefung.zu_lang_kurz')
    case 'minLength':
      if (args.min === 1 && laenge === 0) return uebersetze('meldung.pruefung.pflicht')
      return typeof args.min === 'number'
        ? uebersetze('meldung.pruefung.zu_kurz', { min: args.min, laenge })
        : uebersetze('meldung.pruefung.zu_kurz_kurz')
    case 'required':
      return uebersetze('meldung.pruefung.pflicht')
    case 'pattern':
      // Nie die Formel: „^\d{1,4}$“ sagt niemandem, was ins Feld gehört.
      return f.code === 'color_format_invalid'
        ? uebersetze('meldung.pruefung.farbe')
        : uebersetze('meldung.pruefung.format')
    case 'format':
      if (args.format === 'url' || args.format === 'uri') return uebersetze('meldung.pruefung.url')
      if (args.format === 'email') return uebersetze('meldung.pruefung.email')
      if (args.format === 'date') return uebersetze('meldung.pruefung.datum')
      return uebersetze('meldung.pruefung.format')
    case 'type':
      if (args.expected === 'string') return uebersetze('meldung.pruefung.typ_text')
      if (args.expected === 'number' || args.expected === 'integer') return uebersetze('meldung.pruefung.typ_zahl')
      if (args.expected === 'boolean') return uebersetze('meldung.pruefung.typ_ja_nein')
      return uebersetze('meldung.pruefung.typ')
    case 'additionalProperties':
      return uebersetze('meldung.pruefung.unbekannt')
    case 'enum': {
      const erlaubt = Array.isArray(args.expected)
        ? args.expected.filter((x): x is string => typeof x === 'string').join(', ')
        : ''
      return erlaubt !== ''
        ? uebersetze('meldung.pruefung.auswahl', { erlaubt })
        : uebersetze('meldung.pruefung.auswahl_kurz')
    }
  }
  return f.message
}
